import { useCallback, useEffect, useRef, useState } from "react";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { useT } from "@/i18n";
import { initials, shortDate } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { MessageCircle, GitBranch, Send, RefreshCw, Trash2 } from "lucide-react";

export default function MessagesTab() {
  const { user } = useAuth();
  const t = useT();
  const [shared, setShared] = useState(null);
  const [dms, setDms] = useState(null);
  const [thread, setThread] = useState(null); // { mode, id, otherUserId, title }

  const load = useCallback(async () => {
    try {
      const [sc, inbox] = await Promise.all([
        api.get("/collaboration/shared-chat").then((r) => r.data).catch(() => []),
        api.get("/messages/inbox/list").then((r) => r.data).catch(() => []),
      ]);
      setShared(Array.isArray(sc) ? sc : []);
      setDms(Array.isArray(inbox) ? inbox : []);
    } catch { setShared([]); setDms([]); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const loading = shared === null || dms === null;

  return (
    <div data-testid="soc-messages-tab">
      <div className="mb-4 flex flex-wrap items-center justify-between gap-2">
        <p className="text-sm text-muted-foreground">{t("soc.msg_sub")}</p>
        <Button size="sm" variant="ghost" className="gap-1.5 rounded-full" onClick={load} data-testid="soc-msg-refresh"><RefreshCw className="h-4 w-4" /> {t("common.refresh")}</Button>
      </div>

      {loading ? (
        <div className="space-y-2">{[0, 1, 2].map((i) => <Skeleton key={i} className="h-16 rounded-xl" />)}</div>
      ) : (
        <>
          {shared.length > 0 && (
            <div className="mb-5">
              <p className="mb-2 font-display text-lg text-foreground">{t("soc.msg_shared")}</p>
              <div className="space-y-2">
                {shared.map((c) => {
                  const title = `${c.client_name || "Klient:in"} · ${c.coach_a_name || "Coach"} + ${c.coach_b_name || "Coach"}`;
                  return (
                    <button key={c.id} data-testid={`soc-msg-shared-${c.id}`} onClick={() => setThread({ mode: "shared", id: c.id, title })} className="flex w-full items-center gap-3 rounded-xl border border-border bg-card p-3 text-left hover:bg-accent">
                      <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-primary text-primary-foreground"><GitBranch className="h-4 w-4" /></div>
                      <div className="min-w-0 flex-1">
                        <p className="truncate font-medium text-foreground">{title}</p>
                        <p className="text-xs text-muted-foreground">{t("soc.msg_three_way")}</p>
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          <p className="mb-2 font-display text-lg text-foreground">{t("soc.msg_dms")}</p>
          {dms.length === 0 ? (
            <div className="rounded-2xl border border-dashed border-border bg-card p-10 text-center">
              <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-accent text-accent-foreground"><MessageCircle className="h-6 w-6" /></div>
              <p className="mt-4 text-sm text-muted-foreground">{t("soc.msg_empty")}</p>
            </div>
          ) : (
            <div className="space-y-2">
              {dms.map((th) => (
                <button key={th.user_id} data-testid={`soc-msg-thread-${th.user_id}`} onClick={() => setThread({ mode: "dm", otherUserId: th.user_id, title: th.user_name })} className="flex w-full items-center gap-3 rounded-xl border border-border bg-card p-3 text-left hover:bg-accent">
                  <Avatar className="h-11 w-11">
                    {th.user_photo && <AvatarImage src={th.user_photo} alt={th.user_name} />}
                    <AvatarFallback>{initials(th.user_name)}</AvatarFallback>
                  </Avatar>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center justify-between gap-2">
                      <p className="truncate font-medium text-foreground">{th.user_name}</p>
                      <span className="shrink-0 text-[11px] text-muted-foreground">{shortDate(th.last_at)}</span>
                    </div>
                    <div className="mt-0.5 flex items-center gap-1.5">
                      <span className={`shrink-0 rounded-full px-1.5 py-0.5 text-[9px] font-semibold uppercase text-white ${th.user_role === "coach" ? "bg-primary" : "bg-sky-500"}`}>{th.user_role}</span>
                      <p className={`truncate text-xs ${th.last_deleted ? "italic text-muted-foreground/70" : "text-muted-foreground"}`}>{th.last_deleted ? t("soc.msg_deleted") : th.last_body}</p>
                    </div>
                  </div>
                </button>
              ))}
            </div>
          )}
        </>
      )}

      {thread && <ThreadDialog thread={thread} meId={user?.id} onClose={() => { setThread(null); load(); }} />}
    </div>
  );
}

function ThreadDialog({ thread, meId, onClose }) {
  const t = useT();
  const [msgs, setMsgs] = useState(null);
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const bottomRef = useRef(null);

  const base = thread.mode === "shared"
    ? `/collaboration/shared-chat/${thread.id}/messages`
    : `/messages/${thread.otherUserId}`;

  const load = useCallback(async () => {
    try { const { data } = await api.get(base); setMsgs(Array.isArray(data) ? data : []); }
    catch { setMsgs([]); }
  }, [base]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { bottomRef.current?.scrollIntoView({ behavior: "smooth" }); }, [msgs]);

  const send = async () => {
    const body = text.trim();
    if (!body) return;
    setSending(true);
    try {
      if (thread.mode === "shared") await api.post(base, { body });
      else await api.post("/messages", { to_user_id: thread.otherUserId, body });
      setText(""); await load();
    } catch (err) { toast.error(apiError(err)); }
    finally { setSending(false); }
  };

  const del = async (m) => {
    if (!window.confirm(t("soc.msg_delete_confirm"))) return;
    try {
      if (thread.mode === "shared") await api.delete(`/collaboration/shared-chat/${thread.id}/messages/${m.id}`);
      else await api.delete(`/messages/${m.id}`);
      await load();
    } catch (err) { toast.error(apiError(err)); }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="flex h-[70vh] max-h-[640px] flex-col" data-testid="soc-msg-thread-dialog">
        <DialogHeader className="flex-row items-center justify-between">
          <DialogTitle className="truncate">{thread.title || t("soc.tab_messages")}</DialogTitle>
          <Button size="icon" variant="ghost" className="h-8 w-8 rounded-full" onClick={load}><RefreshCw className="h-4 w-4" /></Button>
        </DialogHeader>
        <div className="-mx-2 flex-1 space-y-2 overflow-y-auto px-2">
          {msgs === null ? (
            <div className="space-y-2">{[0, 1, 2].map((i) => <Skeleton key={i} className="h-10 rounded-xl" />)}</div>
          ) : msgs.length === 0 ? (
            <p className="py-8 text-center text-sm text-muted-foreground">{t("soc.msg_empty_thread")}</p>
          ) : (
            msgs.map((m) => {
              const mine = m.from_user_id === meId;
              const deleted = !!m.deleted;
              return (
                <div key={m.id} className={`group flex items-end gap-1 ${mine ? "justify-end" : "justify-start"}`}>
                  {mine && !deleted && (
                    <button onClick={() => del(m)} data-testid={`soc-msg-delete-${m.id}`} className="mb-1 opacity-0 transition-opacity group-hover:opacity-100">
                      <Trash2 className="h-3.5 w-3.5 text-muted-foreground hover:text-rose-500" />
                    </button>
                  )}
                  <div data-testid={`soc-msg-bubble-${m.id}`} className={`max-w-[80%] rounded-2xl px-3 py-2 text-sm ${mine ? "bg-primary text-primary-foreground" : "bg-secondary text-foreground"}`}>
                    {!mine && m.from_name && <p className="mb-0.5 text-[11px] font-semibold opacity-70">{m.from_name}</p>}
                    <p className="whitespace-pre-wrap break-words">{deleted ? <span className="italic opacity-60">{t("soc.msg_deleted")}</span> : m.body}</p>
                  </div>
                </div>
              );
            })
          )}
          <div ref={bottomRef} />
        </div>
        <div className="flex items-center gap-2 border-t border-border pt-3">
          <Input value={text} onChange={(e) => setText(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }} placeholder={t("soc.msg_input_ph")} data-testid="soc-msg-input" />
          <Button size="icon" className="shrink-0 rounded-full" onClick={send} disabled={sending} data-testid="soc-msg-send"><Send className="h-4 w-4" /></Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
